const API_BASE =
  process.env.REACT_APP_EMAIL_LISTS_API_URL || 'https://kzni23qc9h.execute-api.eu-central-1.amazonaws.com';

export type EmailLists = { whitelist: string[]; blacklist: string[] };

/** Fallback */
export const DEFAULT_EMAIL_BLACKLIST = [
  '126',
  '139',
  '153fdd',
  '1623',
  '163',
  '189',
  'aol',
  'asaud',
  'ceeun',
  'cross.edu.pl',
  'e-mail.edu.pl',
  'fast.edu.pl',
  'foxmail',
  'gmail',
  'gmx',
  'googlemail',
  'guerrillamailblock.com',
  'hotmail',
  'icloud',
  'live',
  'me',
  'moakt.cc',
  'msn',
  'naver',
  'omail.edu.pl',
  'outlook',
  'promail.edu.pl',
  'protonmail',
  'qq',
  'sina',
  'smallntm.lol',
  'tthy.site',
  'vip',
  'yahoo',
] as const;

let cached: EmailLists | null = null;

function normalizeListItems(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items.map(item => (typeof item === 'string' ? item.trim().toLowerCase() : '')).filter(Boolean);
}

export function mergeWithDefaultBlacklist(blacklist: string[] = []): string[] {
  return [...new Set([...DEFAULT_EMAIL_BLACKLIST, ...blacklist])];
}

function buildLists(whitelist: string[], blacklist: string[]): EmailLists {
  return {
    whitelist,
    blacklist: mergeWithDefaultBlacklist(blacklist),
  };
}

export function getDefaultEmailLists(): EmailLists {
  return buildLists([], []);
}

export async function getEmailLists(forceRefresh = false): Promise<EmailLists> {
  if (cached && !forceRefresh) return cached;

  try {
    const [whitelistResponse, blacklistResponse] = await Promise.all([
      fetch(`${API_BASE}/lists/whitelist`),
      fetch(`${API_BASE}/lists/blacklist`),
    ]);

    if (!whitelistResponse.ok || !blacklistResponse.ok) {
      throw new Error('Email lists request failed');
    }

    const [whitelistData, blacklistData] = await Promise.all([
      whitelistResponse.json(),
      blacklistResponse.json(),
    ]);

    cached = buildLists(normalizeListItems(whitelistData.items), normalizeListItems(blacklistData.items));
    return cached;
  } catch (error) {
    console.warn('Email lists fetch failed, using default blacklist fallback', error);
    if (cached) return cached;
    cached = getDefaultEmailLists();
    return cached;
  }
}

export function getCachedEmailLists() {
  return cached ?? getDefaultEmailLists();
}
